import { Menu, UnstyledButton } from '@mantine/core';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { ShipGlyph } from '../../VesselBadge.js';
import type { VesselOption } from '../../VesselCard.js';
import classes from './VoyageHeadCard.module.css';

export interface HeadFact {
  label: string;
  value: string;
  /** Değerin altında küçük satır (ör. ülke). */
  sub?: string;
}

export interface VoyageHeadCardProps {
  /** Bölge adı ekran okuyucuya (sefer no). */
  label: string;
  vessel: VesselOption | null;
  /** Seferin durumu ve rengi; verilmezse rozet yok. */
  status?: { tone: StatusTone; text: string } | null;
  /** Kalkış ve varış tek kutuda (From | To). */
  route?: [HeadFact, HeadFact] | null;
  /** Diğer olgular: kenarlı kutuda, dikey ayırıcılı. */
  facts: readonly HeadFact[];
  /**
   * `full`: Overview, Voyage Performance ve Charter Party (gemi karosu 160×92, ad, IMO, rozet);
   * `compact`: Noon Report ve Leg / Event başlık şeridi (sayfa başlığının sağında).
   */
  variant?: 'full' | 'compact';
  /** Web ofiste filo: kart gemi seçicidir (`Select vessel: <ad>`). */
  vesselOptions?: readonly VesselOption[] | undefined;
  onSelectVessel?: ((id: string) => void) | undefined;
  /** Sağda: sefer seçici, "Edit Voyage" (referansta düğmenin yeri; G4-04). */
  aside?: ReactNode;
}

/**
 * Sefer başlık kartı (gemi-sefer-yonetimi.jpeg üst şerit; M6f G4-03, G4-50): gemi karosu (fotoğraf
 * yerine gemi çizimi, kabul edilen fark), ad, IMO, durum rozeti, kalkış–varış ve olgular. Değerler
 * tek satırda kalır; sığmazsa üç noktayla kısalır ve tamamı `title`'da. Bütün sefer ekranları aynı
 * kartı kullanır; Noon Report ve Leg / Event'te kompakt hâli başlığın sağındadır.
 */
export function VoyageHeadCard({
  label,
  vessel,
  status,
  route,
  facts,
  variant = 'full',
  vesselOptions,
  onSelectVessel,
  aside,
}: VoyageHeadCardProps) {
  const { t } = useTranslation();
  const compact = variant === 'compact';
  const identity = vessel ? (
    <>
      <span className={classes.thumb} aria-hidden>
        <ShipGlyph size={compact ? 40 : 56} />
      </span>
      <span className={classes.identity}>
        <span className={classes.nameRow}>
          <span className={classes.name} title={vessel.name}>
            {vessel.name}
          </span>
          {status && !compact ? (
            <StatusBadge tone={status.tone} withIcon>
              {status.text}
            </StatusBadge>
          ) : null}
        </span>
        {vessel.imo ? (
          <span className={classes.imo}>{t('shell.imo', { imo: vessel.imo })}</span>
        ) : null}
      </span>
    </>
  ) : null;
  const selectable = !!vessel && !!onSelectVessel && (vesselOptions?.length ?? 0) > 1;

  return (
    <section className={classes.card} data-variant={variant} aria-label={label}>
      <div className={classes.inner}>
        {selectable && vessel ? (
          <Menu position="bottom-start" width={260} withinPortal>
            <Menu.Target>
              <UnstyledButton
                className={classes.vessel}
                data-selectable
                aria-label={`${t('shell.selectVessel')}: ${vessel.name}`}
              >
                {identity}
                <IconChevronDown size={16} className={classes.chevron} aria-hidden />
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {(vesselOptions ?? []).map((option) => (
                <Menu.Item
                  key={option.id}
                  onClick={() => onSelectVessel?.(option.id)}
                  rightSection={
                    option.id === vessel.id ? <IconCheck size={14} aria-hidden /> : null
                  }
                >
                  {option.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : (
          <div className={classes.vessel}>{identity}</div>
        )}
        {route || facts.length > 0 ? (
          <div className={classes.factRow}>
            {route ? (
              <dl className={classes.factBox} data-route>
                {route.map((fact) => (
                  <Fact key={fact.label} fact={fact} />
                ))}
              </dl>
            ) : null}
            {facts.length > 0 ? (
              <dl className={classes.factBox}>
                {facts.map((fact) => (
                  <Fact key={fact.label} fact={fact} />
                ))}
              </dl>
            ) : null}
          </div>
        ) : null}
        {aside ? <div className={classes.aside}>{aside}</div> : null}
      </div>
    </section>
  );
}

function Fact({ fact }: { fact: HeadFact }) {
  return (
    <div className={classes.fact}>
      <dt title={fact.label}>{fact.label}</dt>
      <dd title={fact.value}>
        {fact.value}
        {fact.sub ? <span className={classes.sub}>{fact.sub}</span> : null}
      </dd>
    </div>
  );
}
